import {
  isWithinInterval,
  parseISO,
  differenceInMinutes,
  parse,
  format,
  addMinutes,
} from 'date-fns';
import { TBlackout } from '@pages/Physicians/components/PhysiciansTable/types';

const formatTimeTo12Hour = (time: string): string => {
  const [hour, minute] = time.split(':').map(Number);

  const period = hour >= 12 ? 'PM' : 'AM';

  const formattedHour = (hour % 12 || 12).toString().padStart(2, '0');

  return `${formattedHour}:${minute.toString().padStart(2, '0')} ${period}`;
};

const convert12HourTo24Hour = (time: string): string => {
  const [timePart, period] = time.split(' ');

  let hours: number;

  const [h, minutes] = timePart.split(':').map(Number);

  hours = h;

  if (period === 'PM' && hours !== 12) {
    hours += 12;
  } else if (period === 'AM' && hours === 12) {
    hours = 0;
  }

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

const addOneMinute = (time: string): string => {
  const parsedTime = parse(time, 'HH:mm', new Date());

  if (time === '23:59') {
    return format(parsedTime, 'HH:mm');
  }

  const updatedTime = addMinutes(parsedTime, 1);

  return format(updatedTime, 'HH:mm');
};

const formatDate = (dateValue: string) => {
  if (dateValue) {
    return format(parseISO(dateValue), 'MM/dd/yyyy');
  }
  return '';
};

const calculateDurationInMinutes = (
  startTime: string,
  endTime: string,
  isDateInInterval: boolean,
): number => {
  const [startHour, startMinute] = startTime.split(':').map(Number);

  const [endHour, endMinute] = endTime.split(':').map(Number);

  const startInMinutes = startHour * 60 + startMinute;

  const endInMinutes = endHour * 60 + endMinute + 1;

  return endInMinutes - startInMinutes;
};

export const getAppointmentsForDate = (
  selectedDate: Date,
  blackouts: TBlackout[] | undefined,
  physicianId: string,
  startOfDayTime: string,
  endOfDayTime: string,
) =>
  blackouts
    ?.filter((blackout) => blackout.physician_id === physicianId)
    .map((blackout) => {
      const startDate = parseISO(blackout.from);

      const endDate = parseISO(blackout.to);

      const isStartDay = selectedDate.toDateString() === startDate.toDateString();

      const isEndDay = selectedDate.toDateString() === endDate.toDateString();

      const isDateInInterval = isWithinInterval(selectedDate, {
        start: startDate,
        end: endDate,
      });

      if (!isDateInInterval && !isStartDay && !isEndDay) {
        return {
          id: blackout.id,
          startTime: '',
          duration: 0,
        };
      }

      const startTime = isStartDay ? blackout.from_time : convert12HourTo24Hour(startOfDayTime);

      const endTime = isEndDay ? blackout.to_time : convert12HourTo24Hour(endOfDayTime);

      const startOfDay = parse(convert12HourTo24Hour(startOfDayTime), 'HH:mm', new Date());

      const starTimeBlackout = parse(startTime, 'HH:mm', new Date());

      const isFromTimeEarlier = differenceInMinutes(starTimeBlackout, startOfDay) > 0;

      const startTimeForDuration = isFromTimeEarlier
        ? startTime
        : convert12HourTo24Hour(startOfDayTime);

      const durationInMinutes = calculateDurationInMinutes(
        startTimeForDuration,
        endTime,
        isDateInInterval,
      );

      return {
        id: blackout.id,
        startTime: formatTimeTo12Hour(startTimeForDuration),
        duration: durationInMinutes,
        from: formatDate(blackout.from),
        to: formatDate(blackout.to),
        from_time: formatTimeTo12Hour(blackout.from_time),
        to_time: formatTimeTo12Hour(addOneMinute(blackout.to_time)),
      };
    });
